import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Building2, ChevronDown, Download, FileJson, FileSpreadsheet, Lock, Plus, SearchX } from 'lucide-react';
import { AppShell } from '../components/AppShell';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { AppButton } from '../components/ui/AppButton';
import { AppModal } from '../components/ui/AppModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { EmptyState } from '../components/ui/EmptyState';
import { PageContainer } from '../components/ui/PageContainer';
import { PageHeader } from '../components/ui/PageHeader';
import { BuildingForm } from '../components/buildings/BuildingForm';
import { BuildingMapView } from '../components/buildings/BuildingMapView';
import { BuildingPanel } from '../components/buildings/BuildingPanel';
import { BuildingTable } from '../components/buildings/BuildingTable';
import { BUILDING_VIEWS, BuildingsToolbar } from '../components/buildings/BuildingsToolbar';
import { BuildingsSummary } from '../components/buildings/BuildingsSummary';
import { EubsoReportDialog } from '../components/buildings/EubsoReportDialog';
import {
  EPC_RATINGS,
  PERFORMANCE_BANDS,
  SORT_KEYS,
  buildingsToCsv,
  countByBand,
  decorateBuilding,
  matchesFilter,
  sortBuildings,
  summarizePortfolio,
} from '../components/buildings/buildingMetrics';
import { useBuildings } from '../hooks/useBuildings';
import { BADGE_OPTIONS, BUILDING_TYPES, useBuildingEditor } from '../hooks/useBuildingEditor';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../hooks/useTenant';
import { saveCsv } from '../services/download';

const VIEW_IDS = BUILDING_VIEWS.map((view) => view.id);
const PERFORMANCE_FILTERS = ['all', ...PERFORMANCE_BANDS, 'attention'];
const DEFAULTS = { view: 'list', performance: 'all', sort: 'name', dir: 'asc' };

const readParam = (params, key, allowed, fallback) => {
  const value = params.get(key);
  return allowed.includes(value) ? value : fallback;
};

const StateCard = (props) => (
  <div className="card-base" role="status" aria-live="polite">
    <EmptyState {...props} />
  </div>
);

/**
 * Buildings — the building portfolio as a sortable table (or map) under a
 * one-line summary. A building opens in a side panel; Edit and Delete sit in
 * each row's ⋯ menu and in the panel. View, filters, sort and the open
 * building live in the URL (`?performance=poor&sort=performance&building=…`).
 * Actions follow the caller's effective Hub permissions; the API enforces
 * them regardless.
 */
export const Buildings = () => {
  const { has, loading: permissionsLoading } = usePermissions();
  const canRead = has('building:read');
  const canCreate = has('building:create');
  const canUpdate = has('building:update');
  const canDelete = has('building:delete');
  const canEubso = has('eubso:create_job');

  const {
    buildings,
    loading: buildingsLoading,
    addBuilding,
    updateBuilding,
    deleteBuilding,
  } = useBuildings({ enabled: !permissionsLoading && canRead });
  const editor = useBuildingEditor({ addBuilding, updateBuilding });
  const { activeTenant } = useTenant();

  const [params, setParams] = useSearchParams();
  const view = readParam(params, 'view', VIEW_IDS, DEFAULTS.view);
  const performance = readParam(params, 'performance', PERFORMANCE_FILTERS, DEFAULTS.performance);
  const epc = readParam(params, 'epc', EPC_RATINGS, null);
  const sort = readParam(params, 'sort', SORT_KEYS, DEFAULTS.sort);
  const dir = readParam(params, 'dir', ['asc', 'desc'], DEFAULTS.dir);
  const openId = params.get('building');
  const updateParams = (patch) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(patch).forEach(([key, value]) => {
        if (value == null || value === DEFAULTS[key]) next.delete(key);
        else next.set(key, value);
      });
      return next;
    }, { replace: true });
  };

  const [searchTerm, setSearchTerm] = useState('');
  const search = useDebouncedValue(searchTerm.trim().toLowerCase(), 200);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [eubsoOpen, setEubsoOpen] = useState(false);

  const decorated = useMemo(() => (buildings || []).map(decorateBuilding), [buildings]);
  const summary = useMemo(() => summarizePortfolio(decorated), [decorated]);
  const searched = useMemo(
    () => decorated.filter((b) => matchesFilter(b, { search, epc })),
    [decorated, search, epc]
  );
  const bandCounts = useMemo(() => countByBand(searched), [searched]);
  const shown = useMemo(
    () => sortBuildings(searched.filter((b) => matchesFilter(b, { band: performance })), sort, dir),
    [searched, performance, sort, dir]
  );
  const openBuilding = openId ? decorated.find((b) => String(b.id) === openId) || null : null;

  const onSort = (key) =>
    updateParams(sort === key ? { dir: dir === 'asc' ? 'desc' : 'asc' } : { sort: key, dir: 'asc' });
  const openPanel = (building) => updateParams({ building: String(building.id) });
  const closePanel = () => updateParams({ building: null });

  const confirmDelete = async () => {
    const building = pendingDelete;
    setDeleting(true);
    const response = await deleteBuilding(building.id);
    setDeleting(false);
    setPendingDelete(null);
    if (response.success) {
      toast.success(`${building.name} deleted`);
      if (openId === String(building.id)) closePanel();
    } else {
      toast.error(response.error || 'Failed to delete building');
    }
  };

  const exportItems = [
    shown.length > 0 && {
      id: 'csv',
      label: 'Export CSV',
      description: `The ${shown.length} ${shown.length === 1 ? 'building' : 'buildings'} shown`,
      icon: FileSpreadsheet,
      onSelect: () => saveCsv(`buildings-${new Date().toISOString().slice(0, 10)}.csv`, buildingsToCsv(shown)),
    },
    canEubso && {
      id: 'eubso',
      label: 'EU BSO report…',
      description: 'JSON-LD for the EU Building Stock Observatory',
      icon: FileJson,
      onSelect: () => setEubsoOpen(true),
    },
  ].filter(Boolean);

  const renderBody = () => {
    if (permissionsLoading || buildingsLoading) return <SkeletonLoader rows={6} />;
    if (!canRead) {
      return (
        <StateCard
          icon={Lock}
          title="No access to buildings"
          message="Your role in this organisation does not include building access. Ask your administrator if you need it."
        />
      );
    }
    if (decorated.length === 0) {
      return (
        <StateCard
          icon={Building2}
          title="No buildings yet"
          message={canCreate
            ? 'Add your first building to see it on the map and use it in services.'
            : 'No buildings have been shared with you in this organisation yet.'}
          action={canCreate && (
            <AppButton variant="primary" icon={<Plus size={14} />} onClick={editor.openAdd}>
              Add first building
            </AppButton>
          )}
        />
      );
    }
    return (
      <>
        <BuildingsSummary
          summary={summary}
          onShowNeedsAttention={() => updateParams({ performance: 'attention' })}
          onShowEpc={(rating) => updateParams({ epc: rating })}
        />
        <BuildingsToolbar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          band={performance}
          onBandChange={(value) => updateParams({ performance: value })}
          bandCounts={bandCounts}
          totalCount={searched.length}
          epc={epc}
          onClearEpc={() => updateParams({ epc: null })}
          view={view}
          onViewChange={(value) => updateParams({ view: value })}
        />
        {view === 'map' ? (
          <BuildingMapView buildings={shown} openId={openId} onOpen={openPanel} />
        ) : shown.length === 0 ? (
          <StateCard icon={SearchX} title="No matches" message="Adjust the search or filters to see more buildings." />
        ) : (
          <BuildingTable
            buildings={shown}
            sort={sort}
            dir={dir}
            onSort={onSort}
            openId={openId}
            onOpen={openPanel}
            onEdit={canUpdate ? editor.openEdit : undefined}
            onDelete={canDelete ? setPendingDelete : undefined}
          />
        )}
      </>
    );
  };

  return (
    <AppShell title="Buildings">
      <PageContainer>
        <PageHeader
          className="!mb-1"
          title="Buildings"
          actions={canRead && (
            <>
              {decorated.length > 0 && exportItems.length > 0 && (
                <DropdownMenu
                  label="Export"
                  width="w-72"
                  items={exportItems}
                  renderTrigger={({ triggerProps }) => (
                    <AppButton variant="ghost" icon={<Download size={16} />} {...triggerProps}>
                      <span className="inline-flex items-center gap-1">Export <ChevronDown size={14} aria-hidden="true" /></span>
                    </AppButton>
                  )}
                />
              )}
              {canCreate && (
                <AppButton variant="primary" icon={<Plus size={16} />} onClick={editor.openAdd}>
                  Add building
                </AppButton>
              )}
            </>
          )}
        />

        {renderBody()}

        <BuildingPanel
          building={openBuilding}
          onClose={closePanel}
          onEdit={canUpdate ? editor.openEdit : undefined}
          onDelete={canDelete ? setPendingDelete : undefined}
        />

        <AppModal
          open={editor.open}
          onClose={editor.close}
          title={editor.editing ? 'Edit Building' : 'Add Building'}
          description="Complete the form below to create or update a building record."
        >
          <BuildingForm
            editing={Boolean(editor.editing)}
            formData={editor.formData}
            onChange={editor.handleInputChange}
            onSubmit={editor.handleSubmit}
            onCancel={editor.close}
            submitting={editor.submitting}
            buildingTypes={BUILDING_TYPES}
            badgeOptions={BADGE_OPTIONS}
          />
        </AppModal>

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title={pendingDelete ? `Delete ${pendingDelete.name}?` : ''}
          message="This removes the building and its details from the Hub. It cannot be undone."
          confirmLabel="Delete building"
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />

        {eubsoOpen && (
          <EubsoReportDialog
            onClose={() => setEubsoOpen(false)}
            buildingIds={shown.map((b) => b.id)}
            allBuildingsLabel={activeTenant?.name ? `All buildings in ${activeTenant.name}` : 'All buildings'}
            canDownload={has('eubso:export')}
          />
        )}
      </PageContainer>
    </AppShell>
  );
};
